import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { Table } from "react-bootstrap";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import withAuth from "../../utils/withAuth";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import { fetchCallAllDataByIDAPI, filterCallByParams } from "../../utils/api";
import { numberFormat } from "../../utils/numberFormat";
import ReportNavBar from "../../components/ReportNavBar";
import Select from "react-select";

// Skill to preselect by default (from the screenshot you shared)
const PREFERRED_DEFAULT_SKILL = "V_HIN_LIV89_DI";

const DayWiseWithCompare = () => {
    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");

    const [isLoading, setIsLoading] = useState(false);
    const [process, setProcess] = useState([]);
    const [searchSkillRaw, setSearchSkillRaw] = useState("");
    const [searchSkill, setSearchSkill] = useState("");
    const [isCustomRange, setIsCustomRange] = useState(false);

    const defaultStart = useMemo(() => moment().startOf("month"), []);
    const defaultEnd = useMemo(() => moment().endOf("day"), []);
    const [dateRange, setDateRange] = useState({
        startDate: defaultStart,
        endDate: defaultEnd,
    });

    // ===== Compare state =====
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [compareRows, setCompareRows] = useState([]); // date-wise rows for chosen skill
    const [isCompareLoading, setIsCompareLoading] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setSearchSkill(searchSkillRaw.trim()), 250);
        return () => clearTimeout(t);
    }, [searchSkillRaw]);

    const drpRef = useRef(null);

    useEffect(() => {
        if (!ppcid) {
            navigate("/");
            return;
        }
        loadCurrentMonth(ppcid);
    }, [ppcid]);

    const loadCurrentMonth = async (id) => {
        setIsLoading(true);
        try {
            const res = await fetchCallAllDataByIDAPI(
                `ppc-team-get-day-wise-call-report/${id}`
            );
            if (res?.data?.status === 1) {
                const rows = res.data.process || [];
                setProcess(rows);
            } else {
                toast.error(res?.data?.message || "Failed to load process");
            }
        } catch (err) {
            console.error(err);
            toast.error("Network/CORS error while loading process");
        } finally {
            setIsLoading(false);
        }
    };

    // Build skill options from the loaded table data
    const skillOptions = useMemo(() => {
        const set = new Set(
            (process || [])
                .map((r) => String(r.skillname || "").trim())
                .filter(Boolean)
        );
        return Array.from(set)
            .sort()
            .map((s) => ({ value: s, label: s }));
    }, [process]);

    // Default select logic: prefer the skill from your screenshot, else first skill in list
    useEffect(() => {
        if (!skillOptions.length) return;
        if (selectedSkill) return;

        const preferred = skillOptions.find(
            (o) => o.value.toLowerCase() === PREFERRED_DEFAULT_SKILL.toLowerCase()
        );
        if (preferred) {
            setSelectedSkill(preferred);
            return;
        }
        // Fallback: first skill in the list
        setSelectedSkill(skillOptions[0]);
    }, [skillOptions, selectedSkill]);

    // Fetch date-wise compare data whenever skill or dates change
    useEffect(() => {
        if (!ppcid || !selectedSkill) return;

        const start = (dateRange.startDate || moment().startOf("day"))
            .clone()
            .startOf("day");
        const end = (dateRange.endDate || moment().endOf("day"))
            .clone()
            .endOf("day");

        fetchSkillDatewise(ppcid, selectedSkill.value, start, end);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid, selectedSkill, dateRange.startDate, dateRange.endDate]);

    const fetchSkillDatewise = async (ppcid, skillname, start, end) => {
        try {
            setIsCompareLoading(true);
            setCompareRows([]);

            // EXPECTED API: returns
            // { status:1, data:{ skill:'...', rows:[{date, calls, orders, value, total_cost, delivered_value, delivered_count, conv_pct, del_pct, tkt_size}] } }
            const res = await filterCallByParams(
                "ppc-team-get-skill-datewise-report",
                {
                    ppcid,
                    skillname,
                    startDate: start.format("YYYY-MM-DD"),
                    endDate: end.format("YYYY-MM-DD"),
                }
            );

            if (res?.data?.status === 1) {
                setCompareRows(res?.data?.process?.rows || []);
            } else {
                toast.error(
                    res?.data?.message || "No date-wise data found for this skill."
                );
            }
        } catch (e) {
            console.error(e);
            toast.error("Error loading compare data");
        } finally {
            setIsCompareLoading(false);
        }
    };

    const handleFilterByDate = async (_event, picker) => {
        setIsLoading(true);
        const start = picker.startDate.clone().startOf("day");
        const end = picker.endDate.clone().endOf("day");

        setDateRange({ startDate: start, endDate: end });
        setIsCustomRange(true);

        try {
            const res = await filterCallByParams(
                "ppc-team-get-filter-process-report",
                {
                    startDate: start.format("YYYY-MM-DD"),
                    endDate: end.format("YYYY-MM-DD"),
                    ppcid,
                }
            );
            if (res?.data?.status === 1) setProcess(res.data.process || []);
            if (drpRef.current) {
                drpRef.current.innerText = `${start.format(
                    "DD MMM YYYY"
                )} – ${end.format("DD MMM YYYY")}`;
            }
        } catch (error) {
            console.error(error);
            toast.error("Error fetching filtered report");
        } finally {
            setIsLoading(false);
        }
    };

    const handleClear = async () => {
        if (drpRef.current) drpRef.current.innerText = "Select Date Range";
        setIsCustomRange(false);
        setDateRange({ startDate: defaultStart, endDate: defaultEnd });
        await loadCurrentMonth(ppcid);
    };

    // ===== Search over left table =====
    const filteredProcess = useMemo(() => {
        const q = searchSkill.toLowerCase();
        if (!q) return process;
        return (process || []).filter((item) => {
            const fields = [item?.skillname, item?.display];
            return fields.some((f) => String(f || "").toLowerCase().includes(q));
        });
    }, [process, searchSkill]);

    // ===== Totals & Averages (Left table header) =====
    const totals = useMemo(() => {
        const n = (v) => Number(v) || 0;

        return filteredProcess.reduce(
            (acc, item) => {
                acc.orders_count += n(item.orders_count);
                acc.value += n(item.value);
                acc.delivered_value += n(item.delivered_value);
                acc.delivered_count += n(item.delivered_count);
                acc.calls_unique_skill_server += n(item.calls_unique_skill_server);
                acc.calls_unique_exclusive += n(item.calls_unique_exclusive);

                acc.ppc_spend_total += n(item.ppc_spend_total);

                acc.sum_conv_pct += n(item.conv_pct);
                acc.sum_del_pct += n(item.del_pct);
                acc.sum_tkt_size += n(item.tkt_size);
                acc.rows += 1;

                return acc;
            },
            {
                orders_count: 0,
                value: 0,
                delivered_value: 0,
                delivered_count: 0,
                calls_unique_skill_server: 0,
                calls_unique_exclusive: 0,
                ppc_spend_total: 0,

                sum_conv_pct: 0,
                sum_del_pct: 0,
                sum_tkt_size: 0,
                rows: 0,
            }
        );
    }, [filteredProcess]);

    const avgConv = totals.rows ? totals.sum_conv_pct / totals.rows : 0;
    const avgDel = totals.rows ? totals.sum_del_pct / totals.rows : 0;
    const avgTkt = totals.rows ? totals.sum_tkt_size / totals.rows : 0;

    const grandTotalCost = totals.ppc_spend_total;
    const avgCostPerCall =
        totals.calls_unique_exclusive > 0
            ? grandTotalCost / totals.calls_unique_exclusive
            : 0;
    const avgFactor = grandTotalCost > 0 ? totals.value / grandTotalCost : 0;

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::DAY WISE REPORT</title>
            </Helmet>
            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                    </div>
                </div>

                <div className="row">
                    {/* ===== LEFT: main day-wise report (your existing table) ===== */}
                    <div className="col-8 col-lg-8 col-sm-12">
                        <div className="card hrpages">
                            <div className="heading p-2 d-flex flex-wrap gap-2 justify-content-between items-center">
                                <h5 className="mb-0 text-uppercase" style={{ fontWeight: "600" }}>
                                    Day Wise Report
                                </h5>
                                {/* simple search box for left table */}
                                <div className="d-flex align-items-center gap-2">
                                    <input
                                        type="text"
                                        value={searchSkillRaw}
                                        onChange={(e) => setSearchSkillRaw(e.target.value)}
                                        placeholder="Search skill…"
                                        className="form-control form-control-sm"
                                        style={{ minWidth: 220 }}
                                    />
                                </div>
                            </div>

                            <div className="table pagetable table-responsive">
                                <Table
                                    className="table-sm table-fixeds"
                                    responsive
                                    striped
                                    bordered
                                    hover
                                >
                                    <thead>
                                        <tr>
                                            <th
                                                colSpan={1}
                                                className="text-end"
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                Total
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {totals.calls_unique_skill_server}
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {totals.calls_unique_exclusive}
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {totals.orders_count}
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {numberFormat(totals.value)}
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {numberFormat(grandTotalCost)}
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {numberFormat(totals.delivered_value)}
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {totals.delivered_count}
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {avgConv.toFixed(2)}%
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {numberFormat(avgCostPerCall)}
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {avgDel.toFixed(2)}%
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {numberFormat(avgTkt)}
                                            </th>

                                            <th
                                                style={{
                                                    fontWeight: 700,
                                                    fontSize: 14,
                                                    letterSpacing: "0.8px",
                                                }}
                                            >
                                                {avgFactor.toFixed(2)}
                                            </th>
                                        </tr>
                                    </thead>

                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Skill Name</th>
                                            <th>Calls</th>
                                            <th>Orders</th>
                                            <th>Volume</th>
                                            <th>Total Cost</th>
                                            <th>Delivered</th>
                                            <th>Del Count</th>
                                            <th>Conv(%)</th>
                                            <th>Call/Cost</th>
                                            <th>Del(%)</th>
                                            <th>Ticket Size</th>
                                            <th>Factor</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {isLoading ? (
                                            [...Array(10)].map((_, i) => (
                                                <tr key={i}>
                                                    {Array.from({ length: 13 }).map((_, index) => (
                                                        <td key={index}>
                                                            <Skeleton animation="wave" />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : filteredProcess.length > 0 ? (
                                            filteredProcess.map((item, index) => {
                                                const ordersCount = Number(item.orders_count) || 0;
                                                const callsExclusive =
                                                    Number(item.calls_unique_exclusive) || 0;
                                                const value = Number(item.value) || 0;
                                                const totalCost = Number(item.ppc_spend_total) || 0;
                                                const costPerCall =
                                                    callsExclusive > 0
                                                        ? totalCost / callsExclusive
                                                        : 0;
                                                const rowFactor = totalCost > 0 ? value / totalCost : 0;

                                                return (
                                                    <tr key={`${item.skillname}-${index}`}>
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            <Link to={item.process_id}>{item.skillname}</Link>
                                                        </td>
                                                        <td>
                                                            {Number(item.calls_unique_skill_server) || 0}
                                                        </td>
                                                        <td>{ordersCount}</td>
                                                        <td>{numberFormat(value)}</td>
                                                        <td>{numberFormat(totalCost)}</td>
                                                        <td>
                                                            {numberFormat(
                                                                Number(item.delivered_value) || 0
                                                            )}
                                                        </td>
                                                        <td>{Number(item.delivered_count) || 0}</td>
                                                        <td>{Number(item.conv_pct) || 0}</td>
                                                        <td>{numberFormat(costPerCall)}</td>
                                                        <td>{Number(item.del_pct) || 0}</td>
                                                        <td>{numberFormat(Number(item.tkt_size) || 0)}</td>
                                                        <td>{rowFactor.toFixed(2)}</td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={13} className="text-center">
                                                    No matching skills found...
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>

                    {/* ===== RIGHT: Compare by Skill & Date ===== */}
                    <div className="col-4 col-lg-4 col-sm-12">
                        <div className="card hrpages">
                            <div className="p-3 border-bottom">
                                <h6 className="mb-2 text-uppercase" style={{ fontWeight: 600 }}>
                                    Compare by Skill & Date
                                </h6>

                                {/* Skill Selector */}
                                <label className="form-label mb-1">Skill</label>
                                <Select
                                    options={skillOptions}
                                    value={selectedSkill}
                                    onChange={setSelectedSkill}
                                    placeholder="Choose a skill…"
                                    classNamePrefix="react-select"
                                    isClearable
                                />

                                {/* Date Range (re-uses your handler, affects both tables) */}
                                <div className="mt-3">
                                    <label className="form-label mb-1">Date Range</label>
                                    <DateRangePicker
                                        onApply={handleFilterByDate}
                                        initialSettings={{
                                            startDate: dateRange.startDate,
                                            endDate: dateRange.endDate,
                                            maxSpan: { days: 31 },
                                        }}
                                    >
                                        <button className="btn btn-outline-secondary w-100">
                                            {dateRange?.startDate?.format("DD MMM YYYY")} –{" "}
                                            {dateRange?.endDate?.format("DD MMM YYYY")}
                                        </button>
                                    </DateRangePicker>
                                    <button className="btn btn-link p-0 mt-2" onClick={handleClear}>
                                        Clear
                                    </button>
                                </div>
                            </div>

                            {/* Selected Skill header */}
                            <div className="px-3 pt-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">
                                        {selectedSkill
                                            ? `Skill: ${selectedSkill.label}`
                                            : "Select a skill to compare"}
                                    </h6>
                                    {selectedSkill && (
                                        <small className="text-muted">
                                            {dateRange?.startDate?.format("DD MMM")} –{" "}
                                            {dateRange?.endDate?.format("DD MMM, YYYY")}
                                        </small>
                                    )}
                                </div>
                            </div>

                            {/* Date-wise grid */}
                            <div className="table-responsive p-3">
                                <Table
                                    className="table-sm table-fixeds"
                                    responsive
                                    striped
                                    bordered
                                    hover
                                >
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: "nowrap" }}>Date</th>
                                            <th>Calls</th>
                                            <th>Orders</th>
                                            <th>Volume</th>
                                            <th>Cost</th>
                                            <th>Conv(%)</th>
                                            <th>Del(%)</th>
                                            <th>Tkt Size</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isCompareLoading ? (
                                            [...Array(7)].map((_, i) => (
                                                <tr key={i}>
                                                    {Array.from({ length: 8 }).map((_, j) => (
                                                        <td key={j}>
                                                            <Skeleton animation="wave" />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : selectedSkill && compareRows.length > 0 ? (
                                            compareRows.map((r, i) => (
                                                <tr key={i}>
                                                    <td style={{ whiteSpace: "nowrap" }}>
                                                        {moment(r.date).format("DD MMM")}
                                                    </td>
                                                    <td>{Number(r.calls || 0)}</td>
                                                    <td>{Number(r.orders || 0)}</td>
                                                    <td>{numberFormat(Number(r.value || 0))}</td>
                                                    <td>{numberFormat(Number(r.total_cost || 0))}</td>
                                                    <td>{(Number(r.conv_pct || 0)).toFixed(2)}</td>
                                                    <td>{(Number(r.del_pct || 0)).toFixed(2)}</td>
                                                    <td>{numberFormat(Number(r.tkt_size || 0))}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={8} className="text-center">
                                                    {selectedSkill
                                                        ? "No data for this range."
                                                        : "Choose a skill to see date-wise comparison."}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
                {/* End */}
            </ContentWrapper>
        </div>
    );
};

export default withAuth(DayWiseWithCompare);
